import React from 'react'
import ReactDOM from 'react-dom/client'
import './index.css'
import { Provider } from 'react-redux'
import { RouterProvider } from 'react-router-dom'
import { router } from './routing/routes.tsx'
import { store } from '@/storage/store.ts'
import { PersistGate } from 'redux-persist/integration/react'
import { persistor } from '@/app-store/store.ts'
import { AppAlertProvider } from '@/layouts/AppAlertProvider.tsx'
import { QueryClientProvider } from '@tanstack/react-query'
import { setupAxios } from '@/lib/axiosAuth.ts'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { Toaster } from '@/shad/ui/toast/toaster.tsx'
import * as Sentry from '@sentry/react'
import { keycloakAuthService } from './auth/keycloak'
import { queryClient } from '@/lib/queryClient'

setupAxios()

const getEnvironmentFromSubdomain = () => {
  const hostname = window.location.hostname
  const parts = hostname.split('.')

  return parts[0]
}

const enableLogging =
  import.meta.env.VITE_ENABLE_SENTRY_LOGGING === 'true' &&
  process.env.NODE_ENV === 'production'

Sentry.init({
  dsn: 'https://670be619222fcc774c380af0568f0ec4@o4508661293056000.ingest.us.sentry.io/4508803163029504',
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],

  environment: getEnvironmentFromSubdomain(),

  // Tracing
  enabled: enableLogging, //https://docs.sentry.io/platforms/javascript/configuration/options/#enabled

  tracesSampleRate: 1.0, //  Capture 100% of the transactions

  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],

  // Session Replay
  replaysSessionSampleRate: 0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
})

async function bootstrap() {
  await keycloakAuthService().initializeAuth()

  setupAxios()

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <Provider store={store}>
        <PersistGate persistor={persistor}>
          <QueryClientProvider client={queryClient}>
            <AppAlertProvider>
              <RouterProvider router={router} />
              <ReactQueryDevtools initialIsOpen={false} />
            </AppAlertProvider>
            <Toaster />
          </QueryClientProvider>
        </PersistGate>
      </Provider>
    </React.StrictMode>
  )
}

bootstrap()
